import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Upload, message, InputNumber, DatePicker, Select, Space } from 'antd';
import { UploadOutlined, PlusOutlined, EditOutlined } from '@ant-design/icons';
import { paymentService, PAYMENT_TYPES, PAYMENT_METHODS } from '../../services/paymentService';
import dayjs from 'dayjs';

const { Option } = Select;

const Expenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [receipt, setReceipt] = useState(null);
  const [editReceipt, setEditReceipt] = useState(null);

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      const data = await paymentService.getPayments({ type: PAYMENT_TYPES.EXPENSE });
      setExpenses(data);
    } catch (error) {
      message.error('获取支出记录失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpenses();
  }, []);

  const handleAddExpense = () => {
    form.resetFields();
    setReceipt(null);
    setIsModalVisible(true);
  };

  const handleEditExpense = (record) => {
    setEditingExpense(record);
    editForm.setFieldsValue({
      date: dayjs(record.date),
      amount: record.amount,
      method: record.method,
      description: record.description
    });
    setEditReceipt(null);
    setIsEditModalVisible(true);
  };

  const handleSubmit = async (values) => {
    try {
      await paymentService.createPayment({
        amount: values.amount,
        type: PAYMENT_TYPES.EXPENSE,
        description: values.description,
        receipt: receipt,
        date: values.date.toDate(),
        method: values.method
      });
      
      message.success('支出记录添加成功');
      setIsModalVisible(false);
      fetchExpenses();
    } catch (error) {
      message.error('支出记录添加失败: ' + error.message);
    }
  };

  const handleEditSubmit = async (values) => {
    try {
      await paymentService.updatePayment(editingExpense.objectId, {
        amount: values.amount,
        type: PAYMENT_TYPES.EXPENSE,
        description: values.description,
        receipt: editReceipt,
        date: values.date.toDate(),
        method: values.method
      });
      
      message.success('支出记录更新成功');
      setIsEditModalVisible(false);
      setEditingExpense(null);
      fetchExpenses();
    } catch (error) {
      message.error('支出记录更新失败: ' + error.message);
    }
  };

  const paymentMethodOptions = [
    { label: '微信', value: PAYMENT_METHODS.WECHAT },
    { label: '支付宝', value: PAYMENT_METHODS.ALIPAY },
    { label: '现金', value: PAYMENT_METHODS.CASH },
    { label: '其他', value: PAYMENT_METHODS.OTHER }
  ];

  const columns = [
    {
      title: '日期',
      dataIndex: 'date',
      key: 'date',
      render: (date) => dayjs(date).format('YYYY-MM-DD')
    },
    {
      title: '金额',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => `¥${amount.toFixed(2)}`
    },
    {
      title: '支付方式',
      dataIndex: 'method',
      key: 'method',
      render: (method) => paymentMethodOptions.find(opt => opt.value === method)?.label || method
    },
    {
      title: '说明',
      dataIndex: 'description',
      key: 'description'
    },
    {
      title: '收据',
      key: 'receipt',
      render: (_, record) => (
        record.receipt ? 
          <a href={record.receipt} target="_blank" rel="noopener noreferrer">
            查看收据
          </a> : 
          '无'
      )
    },
    {
      title: '创建人',
      dataIndex: 'createdBy',
      key: 'createdBy'
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm')
    },
    {
      title: '操作',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button 
            type="link" 
            icon={<EditOutlined />}
            onClick={() => handleEditExpense(record)}
          >
            编辑
          </Button>
        </Space>
      )
    }
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Button type="primary" onClick={handleAddExpense} icon={<PlusOutlined />}>
          添加支出
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={expenses}
        loading={loading}
        rowKey="objectId"
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title="添加支出记录"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="date"
            label="日期"
            rules={[{ required: true, message: '请选择日期' }]}
            initialValue={dayjs()}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="amount"
            label="金额"
            rules={[{ required: true, message: '请输入金额' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              formatter={value => `¥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/\¥\s?|(,*)/g, '')}
            />
          </Form.Item>

          <Form.Item
            name="method"
            label="支付方式"
            rules={[{ required: true, message: '请选择支付方式' }]}
          >
            <Select placeholder="请选择支付方式">
              {paymentMethodOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label="说明"
            rules={[{ required: true, message: '请输入支出说明' }]}
          >
            <Input.TextArea rows={4} />
          </Form.Item>

          <Form.Item
            label="收据"
            help="请上传收据照片"
          >
            <Upload
              accept="image/*"
              beforeUpload={(file) => {
                setReceipt(file);
                return false;
              }}
              maxCount={1}
            >
              <Button icon={<UploadOutlined />}>上传收据</Button>
            </Upload>
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              提交
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="编辑支出记录"
        open={isEditModalVisible}
        onCancel={() => {
          setIsEditModalVisible(false);
          setEditingExpense(null);
        }}
        footer={null}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleEditSubmit}
        >
          <Form.Item
            name="date"
            label="日期"
            rules={[{ required: true, message: '请选择日期' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="amount"
            label="金额"
            rules={[{ required: true, message: '请输入金额' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              formatter={value => `¥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/\¥\s?|(,*)/g, '')}
            />
          </Form.Item>

          <Form.Item
            name="method"
            label="支付方式"
            rules={[{ required: true, message: '请选择支付方式' }]}
          >
            <Select placeholder="请选择支付方式">
              {paymentMethodOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label="说明"
            rules={[{ required: true, message: '请输入支出说明' }]}
          >
            <Input.TextArea rows={4} />
          </Form.Item>

          <Form.Item
            label="收据"
            help="请上传新的收据照片（可选）"
          >
            <Upload
              accept="image/*"
              beforeUpload={(file) => {
                setEditReceipt(file);
                return false;
              }}
              maxCount={1}
            >
              <Button icon={<UploadOutlined />}>上传新收据</Button>
            </Upload>
            {editingExpense?.receipt && (
              <div style={{ marginTop: 8 }}>
                <span>当前收据：</span>
                <a href={editingExpense.receipt} target="_blank" rel="noopener noreferrer">
                  查看原收据
                </a>
              </div>
            )}
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              更新
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Expenses; 